import React from 'react';
import {
  Box, Paper, Typography, TextField, RadioGroup, FormControlLabel,
  Radio, Checkbox, FormGroup, FormControl, Select, MenuItem,
  Rating, Slider, Button
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';

const FormPreview = ({ formData }) => {
  const renderFieldPreview = (field) => {
    switch (field.type) {
      case 'SHORT_TEXT':
        return <TextField fullWidth variant="standard" placeholder="Your answer" disabled />;
      
      case 'LONG_TEXT':
        return <TextField fullWidth multiline rows={3} variant="standard" placeholder="Your answer" disabled />;
      
      case 'MULTIPLE_CHOICE':
        return (
          <RadioGroup>
            {field.options?.map((opt, i) => (
              <FormControlLabel key={i} value={opt} control={<Radio disabled />} label={opt} />
            ))}
          </RadioGroup>
        );
      
      case 'CHECKBOX':
        return (
          <FormGroup>
            {field.options?.map((opt, i) => (
              <FormControlLabel key={i} control={<Checkbox disabled />} label={opt} />
            ))}
          </FormGroup>
        );
      
      case 'DROPDOWN':
        return (
          <FormControl fullWidth variant="standard">
            <Select disabled displayEmpty value="">
              <MenuItem value="">Choose</MenuItem>
              {field.options?.map((opt, i) => (
                <MenuItem key={i} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      
      case 'RATING':
        return <Rating disabled size="large" />;
      
      case 'SCALE':
        return (
          <Box sx={{ px: 2, pt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption">{field.settings?.scaleMinLabel || field.settings?.scaleMin || 1}</Typography>
              <Typography variant="caption">{field.settings?.scaleMaxLabel || field.settings?.scaleMax || 5}</Typography>
            </Box>
            <Slider
              disabled
              min={field.settings?.scaleMin || 1}
              max={field.settings?.scaleMax || 5}
              marks
              valueLabelDisplay="auto"
            />
          </Box>
        );
      
      case 'DATE':
        return <TextField type="date" variant="standard" disabled sx={{ width: 200 }} />;
      
      case 'TIME':
        return <TextField type="time" variant="standard" disabled sx={{ width: 150 }} />;
      
      case 'NUMBER':
        return <TextField type="number" variant="standard" placeholder="0" disabled sx={{ width: 150 }} />;
      
      case 'EMAIL':
        return <TextField type="email" variant="standard" placeholder="email@example.com" disabled fullWidth />;
      
      case 'PHONE':
        return <TextField type="tel" variant="standard" placeholder="+91 98765 43210" disabled sx={{ width: 200 }} />;
      
      case 'FILE':
        return (
          <Button variant="outlined" startIcon={<CloudUpload />} disabled>
            Upload File
          </Button>
        );
      
      default:
        return <TextField fullWidth variant="standard" placeholder="Your answer" disabled />;
    }
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      {/* Form Header */}
      <Paper 
        sx={{ 
          borderTop: `10px solid ${formData.themeColor || '#673ab7'}`,
          borderRadius: 2,
          overflow: 'hidden',
          mb: 2
        }}
      >
        {formData.headerImage && (
          <Box
            component="img"
            src={formData.headerImage}
            alt="Form header"
            sx={{ width: '100%', height: 200, objectFit: 'cover' }}
          />
        )}
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" fontWeight={500}>
            {formData.title || 'Untitled Form'}
          </Typography>
          {formData.description && (
            <Typography color="textSecondary" sx={{ mt: 1 }}>
              {formData.description}
            </Typography>
          )}
          {formData.settings?.collectEmail && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                * Required
              </Typography>
              <TextField
                fullWidth
                variant="standard"
                label="Email"
                placeholder="Your email"
                required
                disabled
              />
            </Box>
          )}
        </Box>
      </Paper>

      {/* Questions */}
      {formData.fields?.map((field, index) => (
        <Paper key={field.id || index} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
            {field.label || 'Untitled Question'}
            {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
          </Typography>
          
          {field.description && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {field.description}
            </Typography>
          )}

          {renderFieldPreview(field)}
        </Paper>
      ))}

      {/* Submit Button */}
      {formData.fields?.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="contained"
            size="large"
            disabled
            sx={{ 
              bgcolor: formData.themeColor || '#673ab7',
              '&.Mui-disabled': { bgcolor: formData.themeColor || '#673ab7', color: '#fff', opacity: 0.7 }
            }}
          >
            Submit
          </Button>
          <Typography variant="caption" color="textSecondary">
            Never submit passwords through this form
          </Typography>
        </Box>
      )}

      {/* Empty State */}
      {(!formData.fields || formData.fields.length === 0) && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No questions added yet. Add questions to see the preview.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default FormPreview;
